import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClientPortal } from '../../../context/ClientPortalContext';
import { CheckCircleIcon, ExclamationCircleIcon, UserCircleIcon } from '@heroicons/react/24/outline';

const ProfileCompletionCard = () => {
    const navigate = useNavigate();
    const { client } = useClientPortal();

    const { percentage, sections } = useMemo(() => {
        if (!client) return { percentage: 0, sections: [] };

        const checks = [
            { label: 'Name', ok: !!(client.first_name && client.last_name) },
            { label: 'Email', ok: !!client.email },
            { label: 'Phone', ok: !!client.contact_number },
            { label: 'Date of Birth', ok: !!client.date_of_birth },
            { label: 'Country', ok: !!client.country },
            { label: 'Passport', ok: !!client.passport_number },
            { label: 'Address', ok: !!(client.address || client.city) },
            { label: 'Education', ok: !!(client.study_level || client.highest_qualification) }
        ];

        const done = checks.filter((c) => c.ok).length;
        const pct = Math.round((done / checks.length) * 100);

        return { percentage: pct, sections: checks };
    }, [client]);

    const colorClass = percentage === 100
        ? 'from-emerald-500 to-teal-500'
        : percentage >= 75
            ? 'from-indigo-500 to-purple-500'
            : percentage >= 50
                ? 'from-amber-500 to-orange-500'
                : 'from-rose-500 to-pink-500';

    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-slate-900">Profile Completion</h3>
                <button
                    onClick={() => navigate('/portal/profile')}
                    className="text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
                >
                    Edit Profile &rarr;
                </button>
            </div>

            {/* Progress Bar */}
            <div className="relative h-3 bg-slate-100 rounded-full overflow-hidden mb-4">
                <div
                    className={`absolute inset-y-0 left-0 bg-gradient-to-r ${colorClass} transition-all duration-700 rounded-full`}
                    style={{ width: `${percentage}%` }}
                />
            </div>

            <p className="text-sm font-medium text-slate-700 mb-3">
                {percentage === 100 ? (
                    <span className="text-emerald-600">✓ Profile complete!</span>
                ) : (
                    <span>Your profile is <strong>{percentage}%</strong> complete</span>
                )}
            </p>

            {/* Checklist */}
            <ul className="grid grid-cols-2 gap-2 text-sm">
                {sections.map((s) => (
                    <li key={s.label} className="flex items-center gap-2">
                        {s.ok ? (
                            <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                        ) : (
                            <ExclamationCircleIcon className="h-4 w-4 text-slate-300" />
                        )}
                        <span className={s.ok ? 'text-slate-600' : 'text-slate-400'}>{s.label}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default ProfileCompletionCard;
